<script>
  import { onMount } from 'svelte';
  import { useNavigate } from 'svelte-navigator';
  import { filesize } from 'filesize';
  import copy from 'clipboard-copy';

  import {
    Accordion,
    AccordionItem,
    Button,
    Column,
    DataTable,
    Grid,
    InlineNotification,
    Link,
    Loading,
    Row,
    StructuredList,
    StructuredListHead,
    StructuredListRow,
    StructuredListCell,
    StructuredListBody,
    TextArea,
    TooltipIcon,
  } from 'carbon-components-svelte';

  import Copy16 from 'carbon-icons-svelte/lib/Copy16';
  import Info16 from 'carbon-icons-svelte/lib/Information16';
  import Warning16 from 'carbon-icons-svelte/lib/WarningAltFilled16';
  import Launch16 from 'carbon-icons-svelte/lib/Launch16';

  import ApprovalModal from './ApprovalModal.svelte';
  import CountModal from './CountModal.svelte';
  import LoadingButton from '../../../components/LoadingButton.svelte';
  import LoadingSpinner from '../../../components/LoadingSpinner.svelte';
  import RequestCancelModal from './RequestCancelModal.svelte';
  import RestoreTable from './RestoreTable.svelte';
  import StatusIcon from '../../../components/StatusIcon.svelte';
  import TaskExecutionDuration from '../../tasks/TaskExecutionDuration.svelte';

  import { ping, getRestoreRequest, getExecutions } from '../../../services';
  import { convertToLocalDisplayTime, getS3BatchJobUrl, getUrlLabel, isUrl } from '../../../utils';
  import { appConfig, userSession, messagesStore, cacheEntityName } from '../../../stores';

  export let jobId;

  const {
    aws: { region: awsRegion },
  } = $appConfig.data;

  const navigate = useNavigate();
  const tableHeaders = [
    { key: 'path', value: 'Path' },
    { key: 'from', value: 'Start Date' },
    { key: 'to', value: 'End Date' },
    { key: 'totalFiles', value: 'Total Files', display: (value) => value },
    { key: 'totalFileSize', value: 'Total File Size', display: (value) => filesize(value, { standard: 'jedec' }) },
    { key: 'lifetime', value: 'Lifetime (days)' },
    { key: 'restoring', value: 'Restoring' },
  ];
  const requestStatusToIcon = {
    Pending: undefined,
    Approved: true,
    Rejected: false,
    Cancelled: false,
  };

  const restoreStatusToIcon = {
    Completed: true,
    Interrupted: false,
    Failed: false,
    Cancelled: false,
  };

  const responseToText = {
    Rejected: { by: 'Rejected By', at: 'Rejected At', note: 'Rejection Note' },
    Approved: { by: 'Approved By', at: 'Approved At', note: 'Approval Note' },
    Cancelled: { by: 'Cancelled By', at: 'Cancelled At', note: 'Cancellation Note' },
  };

  let hasAccess,
    loading = true,
    restoreRequest,
    haveRequestedSizes = true,
    currentlyRequestingSizes = true,
    relatedTasks = [];

  let modals = {
    approval: { open: false },
    count: { open: false },
    pathStatus: { open: false },
    cancel: { open: false },
  };

  async function validateAccess() {
    try {
      await ping('/api/aws/s3/request');
      hasAccess = true;
    } catch (error) {
      hasAccess = false;
      loading = false;
    }
  }

  async function getTaskExecutions() {
    if (!hasAccess) {
      return;
    }

    try {
      const { results } = await getExecutions({ relatedEntityId: jobId });
      relatedTasks = results;
      haveRequestedSizes = results.some(
        (execution) => execution.relatedEntityId === jobId && execution.taskId === 'object-count' && execution.status === 'completed'
      );
      currentlyRequestingSizes = results.some(
        (execution) => execution.relatedEntityId === jobId && execution.taskId === 'object-count' && execution.status === 'running'
      );
    } catch (error) {
      console.error('[ObjectRestoreApproval] Failed to get task executions!', error);
    }

    try {
      restoreRequest = await getRestoreRequest(jobId);
      cacheEntityName(jobId, getUrlLabel(restoreRequest.ticketId));
      loading = false;
    } catch (error) {
      console.error('[ObjectRestoreApproval] Failed to get restore request!', error);
      restoreRequest = null;
      loading = false;
    }
  }

  function goTo(event, path) {
    event.preventDefault();
    navigate(path);
  }

  function sendToObjectRestore() {
    navigate('/object-restore-request/create', {
      state: {
        selectedEntitlements: restoreRequest.paths.map(({ id, path, from, to, lifetime }) => ({
          id,
          path,
          from,
          to,
          lifetime: lifetime ?? 15,
        })),
      },
    });
  }

  function updatePageData(modification) {
    const { action, data } = modification;

    if (!data) {
      return;
    }

    switch (action) {
      case 'update-approval':
        restoreRequest = {
          ...restoreRequest,
          ...data,
        };
        break;

      default:
        console.warn(`[ObjectRestoreApproval] Unknown action "${action}" on modal close. Define action's expected behavior. Data:`, data);
    }
  }

  function openModal(name, forwardData) {
    modals = {
      ...modals,
      [name]: { open: true, ...forwardData },
    };
  }

  function closeModal(name, modification) {
    if (modification) {
      updatePageData(modification);
    }

    modals = {
      ...modals,
      [name]: { open: false },
    };
  }

  function generateRelatedTasksData(relatedTasks, restoreRequest) {
    const perPathDetails = relatedTasks.reduce((all, task) => {
      if (!['object-count', 'object-restore'].includes(task.taskId) || task.relatedEntityId !== jobId || !task?.data?.logs?.length) {
        return all;
      }

      const {
        taskId,
        status,
        data: { logs },
      } = task;
      for (const { id, path, basePath, pathLogs, subPathLogs, error, restoresInProgress } of logs) {
        const pathId = id ?? path ?? basePath;
        const logsForPath = pathLogs ?? subPathLogs;

        all[pathId] = [
          ...(all[pathId] ?? []),
          {
            ...(error ? { error } : {}),
            ...(logsForPath?.length ? { subPathLogs: logsForPath } : {}),
            ...(restoresInProgress ? { restoresInProgress } : {}),
            fromTask: { taskId, status },
          },
        ];
      }

      return all;
    }, {});

    const hasRunningTasks = relatedTasks.some(
      (task) =>
        (task.taskId === 'object-count' || task.taskId === 'object-restore' || task.taskId === 'object-status') &&
        task.relatedEntityId === jobId &&
        task.status === 'running'
    );

    const validRestoreTask = relatedTasks.find(
      (task) =>
        task.taskId === 'object-restore' && task.relatedEntityId === jobId && (task.status === 'running' || task.status === 'completed')
    );

    const noSizesDetected =
      relatedTasks.some((task) => task.taskId === 'object-count' && task.relatedEntityId === jobId && task.status === 'completed') &&
      restoreRequest?.paths?.every((path) => path.totalFiles === 0);

    const failedRestoreTask = relatedTasks.find(
      (task) =>
        task.taskId === 'object-restore' &&
        task.relatedEntityId === jobId &&
        (task.status === 'failed' || task.status === 'cancelled' || task.status === 'interrupted')
    );

    return [perPathDetails, hasRunningTasks, validRestoreTask, noSizesDetected, failedRestoreTask];
  }

  onMount(validateAccess);

  $: currentUserIsRequestor = $userSession.userProfile.sub === restoreRequest?.createdBy;
  $: runningTaskExecutionsCount = $messagesStore.runningTaskExecutions?.length ?? 0;
  $: getTaskExecutions(hasAccess, runningTaskExecutionsCount);
  $: [perPathDetails, hasRunningTasks, validRestoreTask, noSizesDetected, failedRestoreTask] = generateRelatedTasksData(
    relatedTasks,
    restoreRequest
  );
  $: restoreTask = failedRestoreTask ?? validRestoreTask;
  $: batchRows = restoreRequest?.batches?.map((batch) => ({ ...batch, id: batch.awsBatchId })) ?? [];
</script>

{#if failedRestoreTask}
  <InlineNotification
    kind="error"
    lowContrast
    hideCloseButton
    title="Error:"
    subtitle="The restore request has failed. Please check the related task for more details."
  />
{/if}
{#if hasAccess === undefined}
  <LoadingSpinner loading />
{:else if !hasAccess}
  <InlineNotification
    kind="error"
    lowContrast
    title="Access Denied:"
    subtitle="You do not have access to the Object Restore module."
    hideCloseButton
  />
{:else if loading}
  <LoadingSpinner loading />
{:else if restoreRequest}
  {#if currentUserIsRequestor && restoreRequest.status === 'Pending'}
    <InlineNotification
      kind="warning"
      lowContrast
      title="Info:"
      subtitle="You cannot approve your own request; please share this request for approval."
      hideCloseButton
    />
  {/if}
  <div class="restore-read">
    <Grid noGutterLeft noGutterRight padding>
      <Row>
        <Column sm={4} md={8} lg={8}>
          <h2 class="column-heading">Request Details</h2>
          <StructuredList condensed flush class="mst-structured-list--truncate">
            <StructuredListHead>
              <StructuredListRow head>
                <StructuredListCell head>Attribute</StructuredListCell>
                <StructuredListCell head>Value</StructuredListCell>
              </StructuredListRow>
            </StructuredListHead>
            <StructuredListBody>
              <StructuredListRow>
                <StructuredListCell noWrap>Ticket</StructuredListCell>
                <StructuredListCell>
                  <div class="id-controls">
                    <div class="id-text">
                      {restoreRequest.ticketId}
                    </div>
                    <Button
                      portalTooltip
                      class="copy-button"
                      hasIconOnly
                      icon={Copy16}
                      iconDescription="Copy to clipboard"
                      kind="ghost"
                      size="small"
                      tooltipPosition="top"
                      tooltipAlignment="end"
                      on:click={() => copy(restoreRequest.ticketId)}
                    />
                    {#if isUrl(restoreRequest.ticketId)}
                      <Button
                        portalTooltip
                        icon={Launch16}
                        iconDescription="Open in new tab"
                        kind="ghost"
                        size="small"
                        tooltipPosition="top"
                        tooltipAlignment="end"
                        href={restoreRequest.ticketId}
                        target="_blank"
                        rel="noopener noreferrer"
                      />
                    {/if}
                  </div>
                </StructuredListCell>
              </StructuredListRow>
              <StructuredListRow>
                <StructuredListCell noWrap>Requested By</StructuredListCell>
                <StructuredListCell>
                  <Link href={`/user/${restoreRequest.createdBy}`} on:click={(event) => goTo(event, `/user/${restoreRequest.createdBy}`)}>
                    {restoreRequest.createdByDisplayName}
                  </Link>
                </StructuredListCell>
              </StructuredListRow>
              <StructuredListRow>
                <StructuredListCell noWrap>Requested At</StructuredListCell>
                <StructuredListCell>
                  {convertToLocalDisplayTime(restoreRequest.createdAt)}
                </StructuredListCell>
              </StructuredListRow>
              {#if restoreRequest.approvedBy}
                <StructuredListRow>
                  <StructuredListCell noWrap>{responseToText[restoreRequest.status].by}</StructuredListCell>
                  <StructuredListCell>
                    <Link
                      href={`/user/${restoreRequest.approvedBy}`}
                      on:click={(event) => goTo(event, `/user/${restoreRequest.approvedBy}`)}
                    >
                      {restoreRequest.approvedByDisplayName}
                    </Link>
                  </StructuredListCell>
                </StructuredListRow>
              {/if}
            </StructuredListBody>
          </StructuredList>
        </Column>
        <Column sm={4} md={8} lg={8}>
          <div class="status-wrapper">
            <div>
              <h2 class="column-heading">Request Status</h2>
              <div class={`status ${restoreRequest.status.toLowerCase()}`}>
                <StatusIcon valid={requestStatusToIcon[restoreRequest.status]} />
                <strong>{restoreRequest.status.toUpperCase()}</strong>
              </div>
              {#if restoreRequest.approvedAt}
                <em>{convertToLocalDisplayTime(restoreRequest.approvedAt)}</em>
              {/if}
            </div>
            {#if restoreRequest.status === 'Approved' && restoreTask}
              <div>
                <h2 class="column-heading">
                  Restore Status
                  <TooltipIcon
                    tooltipText="Status of the MACE restore task. Final object availability is subject to AWS restore processing times."
                    direction="right"
                    icon={Info16}
                    portalTooltip
                  />
                </h2>
                <div class={`status ${restoreTask.status}`}>
                  {#if restoreTask.status === 'running'}
                    <Loading withOverlay={false} small />
                  {:else}
                    <StatusIcon valid={restoreStatusToIcon[restoreTask.statusDescription]} />
                  {/if}
                  <strong>{restoreTask.status.toUpperCase()}</strong>
                  {#if Object.keys(perPathDetails).length}
                    <div class="warning">
                      <TooltipIcon
                        tooltipText="One or more paths have returned logs. Check 'Paths' table for expandable rows or 'Related Tasks' for more details."
                        direction="right"
                        icon={Warning16}
                        portalTooltip
                      />
                    </div>
                  {/if}
                </div>
                {#if restoreTask.status !== 'running'}
                  <em>{convertToLocalDisplayTime(restoreTask.updatedAt ?? restoreTask.createdAt)}</em>
                {/if}
              </div>
            {/if}
          </div>
          <div class="textarea">
            <TextArea
              id="note"
              name="note"
              labelText="Request Note"
              value={restoreRequest.note}
              readonly
              placeholder="No request note provided."
            />
          </div>
          {#if restoreRequest.approvedBy}
            <div class="textarea">
              <TextArea
                id="approval-note"
                name="approval-note"
                labelText={responseToText[restoreRequest.status].note}
                value={restoreRequest.approvalMessage}
                readonly
                placeholder="No note provided."
              />
            </div>
          {/if}
        </Column>
      </Row>
    </Grid>
    <Accordion>
      <AccordionItem disabled={!relatedTasks?.length}>
        <svelte:fragment slot="title">
          <div>Related Tasks ({relatedTasks.length ?? 0})</div>
          {#if hasRunningTasks}
            <Loading withOverlay={false} small />
          {/if}
        </svelte:fragment>
        <DataTable
          headers={[
            { key: 'task.name', value: 'Task Name' },
            { key: 'statusDescription', value: 'Status' },
            { key: 'createdByDisplayName', value: 'Triggered By' },
            { key: 'createdAt', value: 'Triggered At', display: convertToLocalDisplayTime },
            { key: 'executionTime', value: 'Execution Time' },
          ]}
          size="compact"
          rows={relatedTasks}
        >
          <span slot="cell" let:cell let:row>
            {#if cell.key === 'executionTime'}
              <TaskExecutionDuration {row} />
            {:else if cell.key === 'statusDescription' && cell.value === 'Running'}
              <div class="running-cell-wrapper">
                <div>Running...</div>
                <LoadingSpinner centered={false} compact inlineLeftMargin withOverlay={false} />
              </div>
            {:else if cell.display}
              {cell.display(cell.value, row)}
            {:else}
              {cell.value}
            {/if}
          </span>
        </DataTable>
        <Button size="small" kind="ghost" on:click={() => navigate(`/tasks?relatedEntityId=${restoreRequest.id}#executions`)}>
          View on Task List
        </Button>
      </AccordionItem>
      <AccordionItem disabled={!restoreRequest.batches?.length}>
        <svelte:fragment slot="title">
          <div>AWS Batch Operations Jobs ({restoreRequest.batches?.length ?? 0})</div>
        </svelte:fragment>
        <DataTable
          headers={[
            { key: 'awsBatchId', value: 'Job ID' },
            { key: 'tier', value: 'Tier' },
            { key: 'lifetime', value: 'Lifetime (days)' },
            { key: 'createdAt', value: 'Created At', display: convertToLocalDisplayTime },
          ]}
          size="compact"
          rows={batchRows}
        >
          <span slot="cell" let:cell let:row>
            {#if cell.key === 'awsBatchId'}
              <Link class="open-aws-link" href={getS3BatchJobUrl(awsRegion, row.awsBatchId)} target="_blank" rel="noopener noreferrer">
                {row.awsBatchId}
                <Launch16 />
              </Link>
            {:else if cell.display}
              {cell.display(cell.value)}
            {:else}
              {cell.value ?? '—'}
            {/if}
          </span>
        </DataTable>
      </AccordionItem>
    </Accordion>
    <h2 class="column-heading">Paths</h2>
    <RestoreTable {tableHeaders} {perPathDetails} paths={restoreRequest.paths} fetchingSizes={currentlyRequestingSizes} />
  </div>
  {#if restoreRequest.status === 'Pending' && !currentUserIsRequestor && !validRestoreTask}
    <div class="action">
      <div class="action-items">
        <LoadingButton
          size="field"
          kind="secondary"
          on:click={() => openModal('count')}
          isLoading={currentlyRequestingSizes}
          disabled={haveRequestedSizes || currentlyRequestingSizes}
        >
          Get Files Details
        </LoadingButton>
        <div class="action action-main">
          <Button
            disabled={currentlyRequestingSizes}
            size="field"
            kind="danger"
            on:click={() => openModal('approval', { status: 'Rejected' })}
          >
            Reject
          </Button>
          <Button disabled={currentlyRequestingSizes} size="field" on:click={() => openModal('approval', { status: 'Approved' })}>
            Approve
          </Button>
        </div>
      </div>
    </div>
  {/if}
  {#if restoreRequest.status === 'Pending' && currentUserIsRequestor}
    <div class="action">
      <div class="action-items">
        <Button disabled={currentlyRequestingSizes} size="field" kind="danger" on:click={() => openModal('cancel')}>Cancel Request</Button>
      </div>
    </div>
  {/if}
  {#if restoreRequest.status !== 'Pending' && !hasRunningTasks}
    <div class="action">
      <div class="action-items">
        <Button size="field" kind="tertiary" on:click={sendToObjectRestore}>Request Again</Button>
      </div>
    </div>
  {/if}
{:else}
  <InlineNotification kind="error" lowContrast title="Error:" subtitle="Restore request not found." hideCloseButton />
{/if}

{#if modals.approval.open}
  <ApprovalModal
    open
    status={modals.approval.status}
    blocked={currentlyRequestingSizes || !!validRestoreTask}
    {restoreRequest}
    {noSizesDetected}
    on:close={({ detail: data }) => closeModal('approval', { action: 'update-approval', data })}
  />
{/if}

{#if modals.count.open}
  <CountModal
    open
    blocked={haveRequestedSizes || currentlyRequestingSizes || !!validRestoreTask}
    {restoreRequest}
    on:close={() => closeModal('count')}
  />
{/if}

{#if modals.cancel.open}
  <RequestCancelModal
    open
    blocked={currentlyRequestingSizes || !!validRestoreTask}
    {restoreRequest}
    on:close={({ detail: data }) => closeModal('cancel', { action: 'update-approval', data })}
  />
{/if}

<style>
  .column-heading {
    font-size: 1.25rem;
    font-weight: 400;
    letter-spacing: 0;
    line-height: 1.4;
    margin-bottom: 0.5rem;
  }

  .column-heading + :global(.bx--structured-list) {
    margin-bottom: 2.5rem;
  }
  .column-heading + :global(.bx--structured-list:last-child) {
    margin-bottom: 0.5rem;
  }
  .id-controls :global(.bx--btn) {
    min-height: unset;
    padding-top: 0;
  }
  .id-controls :global(.bx--btn svg > path) {
    fill: var(--cds-link-01) !important;
  }

  .id-controls {
    display: flex;
    align-items: center;
    gap: 0.25rem;
  }

  .id-text {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .status-wrapper {
    display: flex;
    margin-bottom: 0.5rem;
  }

  .status-wrapper em {
    font-size: 0.75rem;
    color: var(--cds-text-02);
    margin-top: 0.25rem;
  }

  .status-wrapper > div:nth-child(2) {
    margin-left: 1rem;
  }

  .status-wrapper .warning {
    margin-left: 0.5rem;
  }

  .status-wrapper .warning :global(svg) {
    fill: #a86d00;
  }

  .textarea :global(.bx--label) {
    margin-bottom: 0.25rem;
  }

  .textarea :global(.bx--text-area__wrapper) {
    margin-bottom: 0.5rem;
  }

  .status {
    display: flex;
    align-items: center;
  }

  .status.pending {
    color: var(--cds-inverse-support-03);
    fill: var(--cds-inverse-support-03);
  }

  .status.approved,
  .status.completed {
    color: var(--cds-support-02);
    fill: var(--cds-support-02);
  }

  .status.rejected,
  .status.cancelled,
  .status.interrupted,
  .status.failed {
    color: var(--cds-danger-02);
    fill: var(--cds-danger-01);
  }

  .status.running {
    color: var(--cds-inverse-support-03);
  }

  .status.running :global(.bx--loading--small) {
    margin: 0.2rem;
  }

  .status :global(.status-icon) {
    margin-right: 0.25rem;
  }

  .action {
    margin-top: 1rem;
  }

  .action-main {
    display: flex;
    justify-content: flex-end;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 0.25rem;
  }

  .action-items {
    display: flex;
    flex-direction: column;
  }

  .action-items :global(.bx--btn--secondary),
  .action-items :global(.bx--btn--tertiary),
  .action-items :global(.bx--btn--danger) {
    justify-self: flex-end;
    margin-left: auto;
  }

  .action-items :global(.bx--btn-set) {
    justify-content: flex-end;
    margin-top: 1rem;
  }

  .running-cell-wrapper {
    display: inline-flex;
  }

  .restore-read :global(.bx--accordion) {
    margin: 1rem 0 1rem 0;
  }

  .restore-read :global(.bx--accordion__title) {
    display: flex;
    align-items: center;
  }

  .restore-read :global(.bx--accordion__title .bx--loading) {
    margin-left: 0.5rem;
  }

  .restore-read :global(.bx--accordion__content .bx--btn--ghost) {
    margin-top: 0.5rem;
  }

  .restore-read :global(.open-aws-link) {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .restore-read :global(.open-aws-link svg) {
    flex: none;
  }
</style>
